import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createWebhookToken, deleteWebhookAlias, deleteWebhookToken, setWebhookAlias,
} from '../api/flows';
import { FlowResponse } from '../types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * The Trigger is a separate service on its own port and nginx proxies only /api/, so the SPA's
 * own origin is a dead webhook URL.
 *
 * Runtime config first, exactly like the Azure IDs: import.meta.env is baked in at `npm run
 * build`, and the frontend image is built without it, so a bundled value would be empty in every
 * container deployment. The VITE_ variable remains for `npm start`, which reads .env.
 */
function triggerBase(): string | null {
  const configured =
    window.__APP_CONFIG__?.triggerUrl
    || import.meta.env.VITE_TRIGGER_URL
    || '';
  return configured.replace(/\/$/, '') || null;
}

interface Props {
  flow: FlowResponse;
  /** Overrides the configured trigger base URL. Used by tests. */
  triggerBaseUrl?: string;
  onClose: () => void;
}

export function WebhookSecurityModal({ flow, triggerBaseUrl, onClose }: Props) {
  const qc = useQueryClient();
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aliasInput, setAliasInput] = useState(flow.webhookAlias ?? '');

  const isProtected = flow.webhookAuth === 'Token';
  // Falling back to the SPA origin would print a URL that silently returns the app, so say so
  // instead of inventing one.
  const baseUrl = triggerBaseUrl ?? triggerBase();
  const idUrl = `${baseUrl ?? window.location.origin}/webhooks/${flow.id}`;
  const aliasUrl = flow.webhookAlias
    ? `${baseUrl ?? window.location.origin}/webhooks/${flow.webhookAlias}`
    : null;
  const webhookUrl = aliasUrl ?? idUrl;
  const triggerUrlUnknown = baseUrl === null;

  // Show the real token while it is still on screen, so the example can be copied and run as is.
  // Afterwards only the prefix is left, so the example becomes a shape to fill in.
  const exampleToken = issuedToken ?? `${flow.webhookTokenPrefix ?? 'whk_'}…`;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['flows', flow.id] });
    qc.invalidateQueries({ queryKey: ['flows'] });
  };

  const create = useMutation({
    mutationFn: () => createWebhookToken(flow.id),
    onSuccess: res => {
      setIssuedToken(res.token);
      setError(null);
      refresh();
    },
    onError: () => setError('Could not create the token.'),
  });

  const remove = useMutation({
    mutationFn: () => deleteWebhookToken(flow.id),
    onSuccess: () => {
      setIssuedToken(null);
      setError(null);
      refresh();
    },
    onError: () => setError('Could not make the webhook public.'),
  });

  const saveAlias = useMutation({
    mutationFn: () => setWebhookAlias(flow.id, aliasInput),
    onSuccess: res => {
      setAliasInput(res.webhookAlias ?? '');
      setError(null);
      refresh();
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status?: number; data?: { error?: string } } })
        ?.response;
      setError(response?.status === 409 || response?.status === 400
        ? response.data?.error ?? 'Invalid alias.'
        : 'Could not save the alias.');
    },
  });

  const removeAlias = useMutation({
    mutationFn: () => deleteWebhookAlias(flow.id),
    onSuccess: () => {
      setAliasInput('');
      setError(null);
      refresh();
    },
    onError: () => setError('Could not remove the alias.'),
  });

  const busy = create.isPending || remove.isPending
    || saveAlias.isPending || removeAlias.isPending;
  const aliasUnchanged = aliasInput.trim().toLowerCase() === (flow.webhookAlias ?? '');

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Webhook security</DialogTitle>
          <DialogDescription>
            A public webhook can be triggered by anyone who knows the flow id. Protect it with a
            token, sent either as an <code>X-Webhook-Token</code> header or a{' '}
            <code>token</code> query parameter.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          <div className="space-y-1">
            <Label>Webhook URL</Label>
            <Input readOnly value={webhookUrl} className="font-mono text-xs" />
            {aliasUrl && (
              <p className="text-xs text-muted-foreground">
                Also reachable by flow id: <code>{idUrl}</code>
              </p>
            )}
            {triggerUrlUnknown && (
              <p className="text-xs text-destructive">
                The Trigger URL is not configured, so this falls back to this app's address —
                which does not serve webhooks. Set <code>FLOWFORGE_TRIGGER_URL</code> on the
                frontend container (or <code>VITE_TRIGGER_URL</code> when running{' '}
                <code>npm start</code>).
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="webhook-alias">Alias</Label>
            <div className="flex gap-2">
              <Input
                id="webhook-alias"
                value={aliasInput}
                onChange={e => setAliasInput(e.target.value)}
                placeholder="e.g. from-jira-to-hq"
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                onClick={() => saveAlias.mutate()}
                disabled={busy || aliasInput.trim() === '' || aliasUnchanged}
              >
                Save alias
              </Button>
              {flow.webhookAlias && (
                <Button variant="outline" onClick={() => removeAlias.mutate()} disabled={busy}>
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Lower-case letters, digits and hyphens. A readable alias is easy to guess, so protect
              the webhook with a token if it should not be public. Changes reach the trigger within
              30 seconds.
            </p>
          </div>

          <div className="text-sm">
            Status:{' '}
            {isProtected ? (
              <span className="font-medium">
                Token required
                {flow.webhookTokenPrefix && (
                  <span className="text-muted-foreground font-mono">
                    {' '}({flow.webhookTokenPrefix}…)
                  </span>
                )}
              </span>
            ) : (
              <span className="font-medium">Public — no token required</span>
            )}
          </div>

          {issuedToken && (
            <div className="space-y-1">
              <Label>New token</Label>
              <Input readOnly value={issuedToken} className="font-mono text-xs" />
              <p className="text-xs text-muted-foreground">
                Copy it now — only its hash is stored, so it cannot be shown again.
              </p>
            </div>
          )}

          {isProtected && (
            <div className="space-y-1">
              <Label>How to send the token</Label>
              <pre className="bg-muted rounded p-2 text-xs font-mono whitespace-pre-wrap break-words">
{`curl -X POST "${webhookUrl}" \\
  -H "X-Webhook-Token: ${exampleToken}" \\
  -H "Content-Type: application/json" \\
  -d '{"hello":"world"}'`}
              </pre>
              <p className="text-xs text-muted-foreground">
                For callers that cannot set headers, pass it as a query parameter instead:
              </p>
              <pre className="bg-muted rounded p-2 text-xs font-mono whitespace-pre-wrap break-all">
{`${webhookUrl}?token=${exampleToken}`}
              </pre>
              <p className="text-xs text-muted-foreground">
                A missing or wrong token gets <code>401 Unauthorized</code> and no execution is
                created.
              </p>
            </div>
          )}

          {isProtected && (
            <p className="text-xs text-muted-foreground">
              The trigger caches flows for up to 30 seconds, so a rotated or removed token can stay
              usable for that long.
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {isProtected && (
            <Button variant="outline" onClick={() => remove.mutate()} disabled={busy}>
              Make public
            </Button>
          )}
          <Button onClick={() => create.mutate()} disabled={busy}>
            {isProtected ? 'Rotate token' : 'Require a token'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
